import { ReactElement, ReactNode } from 'react';
import { type ToastOptions } from 'react-toastify';

export const TOAST_EVENT_NAME = 'fly:toast' as const;

export type ToastEvent = {
  type: ToastType;
  size?: '1' | '2' | '3';
  variant?: 'soft' | 'surface' | 'outline';
  icon?: string;
  content: ReactNode;
  toastOptions: Omit<ToastOptions, 'icon'>;
};

type ToastType =
  | 'neutral'
  | 'error'
  | 'success'
  | 'info'
  | 'warning'
  | 'custom';

/** Fly Callout presentation; `icon` is a sprite name, not react-toastify's ToastIcon. */
interface FlyToastOptions {
  size?: '1' | '2' | '3';
  variant?: 'soft' | 'surface' | 'outline';
  icon?: string;
}

export type ToastServiceOptions = Omit<ToastOptions, 'icon'> & FlyToastOptions;

class ToastService {
  private createToast(
    type: ToastType,
    content: ReactElement | string,
    options: ToastServiceOptions = {}
  ) {
    const {
      position = 'top-right',
      autoClose = 3000,
      draggable = false,
      size,
      variant,
      icon,
      ...toastOptions
    } = options;

    window.dispatchEvent(
      new CustomEvent(TOAST_EVENT_NAME, {
        detail: {
          type,
          size,
          variant,
          icon,
          content,
          toastOptions: {
            position,
            autoClose,
            hideProgressBar: true,
            containerId: 'custom',
            pauseOnHover: true,
            draggable,
            ...toastOptions,
          },
        },
      })
    );
  }

  custom(content: ReactElement | string, options: ToastServiceOptions = {}) {
    return this.createToast('custom', content, options);
  }

  neutral(content: ReactElement | string, options: ToastServiceOptions = {}) {
    return this.createToast('neutral', content, options);
  }

  error(content: ReactElement | string, options: ToastServiceOptions = {}) {
    return this.createToast('error', content, options);
  }

  success(content: ReactElement | string, options: ToastServiceOptions = {}) {
    return this.createToast('success', content, options);
  }

  info(content: ReactElement | string, options: ToastServiceOptions = {}) {
    return this.createToast('info', content, options);
  }

  warning(content: ReactElement | string, options: ToastServiceOptions = {}) {
    return this.createToast('warning', content, options);
  }
}

export const toastService = new ToastService();
